import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Print as PrintIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  Payment as PaymentIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  ReceiptLong as ReceiptLongIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { getTicketById, deleteTicket, recordTicketPayment } from '../services/TicketDeCaisseService';
import { fetchPaymentMethods } from '../../../app/store/slices/paymentMethodSlice';
import TICKET_TYPE, { getTicketTypeLabel, getTicketTypeColor, normalizeTicketType } from '../../../constants/ticketTypes';
import PreviewDialog from '../../pos/components/PreviewDialog';
import A4PreviewDialog from '../../pos/components/A4PreviewDialog';
import { generateSaleTicketPreview, generateRepairTicketPreview } from '../../pos/utils/ticketPreviewUtils';
import { A4InvoiceTemplate } from '../../pos/components/A4InvoiceTemplate';
import PrintOptionsDialog from '../../pos/components/PrintOptionsDialog';

const TicketDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Get store info from Redux store
  const storeInfo = useSelector((state) => state.company?.companyInfo || state.auth?.companyInfo);

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteButton, setShowDeleteButton] = useState(false);

  // Payment dialog state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);

  // Get payment methods from Redux
  const paymentMethodsFromStore = useSelector((state) => state.paymentMethod.paymentMethods) || [];

  // Print dialogs
  const [printOptionsOpen, setPrintOptionsOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [currentFormat, setCurrentFormat] = useState('receipt');

  useEffect(() => {
    fetchTicketDetails();
    // Fetch payment methods if not already loaded
    if (paymentMethodsFromStore.length === 0) {
      dispatch(fetchPaymentMethods());
    }
  }, [id, dispatch, paymentMethodsFromStore.length]);

  const fetchTicketDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getTicketById(id);

      // Handle different response structures
      let ticketData = null;
      if (response?.payload) {
        ticketData = response.payload;
      } else if (response?.data?.payload) {
        ticketData = response.data.payload;
      } else if (response?.data) {
        ticketData = response.data;
      } else {
        ticketData = response;
      }

      setTicket(ticketData);
    } catch (error) {
      console.error('❌ Error fetching ticket details:', error);
      setError(error.message || 'Failed to load ticket details');
      toast.error(t('failed_to_load_ticket') || 'Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/tickets');
  };

  const handleEdit = () => {
    navigate(`/tickets/edit/${id}`);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);

      await deleteTicket(id);

      toast.success(t('ticket_deleted_success') || 'Ticket deleted successfully');
      setDeleteDialogOpen(false);
      navigate('/tickets');
    } catch (error) {
      console.error('❌ Error deleting ticket:', error);
      toast.error(error.response?.data?.message || t('failed_to_delete_ticket') || 'Failed to delete ticket');
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleConvertToInvoice = () => {
    navigate('/invoices/add', { state: { fromTicket: ticket } });
  };

  const handleOpenPaymentDialog = () => {
    const remainingAmount = (ticket.TotalAmountInclVATAfterDiscount || ticket.TotalAmountInclVAT) - (ticket.PaidAmount || 0);
    setPaymentAmount(remainingAmount.toFixed(2));
    setPaymentMethod(paymentMethodsFromStore[0]?.name || '');
    setPaymentDialogOpen(true);
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialogOpen(false);
    setPaymentAmount('');
    setPaymentMethod('');
    setPaymentReference('');
    setPaymentNotes('');
  };

  const handleSubmitPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error(t('please_enter_valid_amount') || 'Please enter a valid payment amount');
      return;
    }
    if (!paymentMethod) {
      toast.error(t('please_select_payment_method') || 'Please select a payment method');
      return;
    }

    try {
      setPaymentLoading(true);

      const paymentData = {
        TicketId: parseInt(id),
        PaymentMethod: paymentMethod,
        PaymentAmount: parseFloat(paymentAmount),
        PaymentDate: paymentDate,
        PaymentReference: paymentReference || null,
        Notes: paymentNotes || null
      };

      const response = await recordTicketPayment(paymentData);
      toast.success(t('payment_recorded_successfully') || 'Payment recorded successfully');

      // Refresh ticket data
      await fetchTicketDetails();
      handleClosePaymentDialog();
    } catch (error) {
      console.error('Error recording payment:', error);
      toast.error(error.response?.data?.message || t('failed_to_record_payment') || 'Failed to record payment');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handlePrintClick = () => {
    // ✅ Directly preview ticket instead of showing options dialog
    handleFormatSelection('receipt', 'preview');
  };

  const handleFormatSelection = async (format, action) => {
    setPrintOptionsOpen(false);
    setCurrentFormat(format);


    // ✅ Prepare cart data from ticket line items - use exact database values without recalculation
    const cart = (ticket.LineItems || ticket.Items || []).map(item => ({
      Id: item.ProductId || item.Id,
      Name: item.ProductName || item.ServiceName || item.Name || 'Product',
      Description: item.ServiceDescription || item.Description || '',
      // ✅ Use exact values from database - these are already calculated and saved
      Price: parseFloat(item.UnitPriceExclTax || item.Price || 0),
      PriceInclVAT: parseFloat(item.UnitPriceInclVat || item.UnitPriceInclVAT || 0),
      Quantity: parseFloat(item.Quantity || 1),
      VAT: parseFloat(item.VATRate || item.VAT || 21),
      SerialNumber: item.ProductSerial || item.SerialNumber || '',
      // ✅ Include pre-calculated amounts from database to prevent recalculation differences
      Subtotal: parseFloat(item.Subtotal || 0),
      VATAmount: parseFloat(item.VATAmount || 0),
      TotalAmountInclVAT: parseFloat(item.TotalAmountInclVAT || 0),
      TotalAmountInclVATAfterDiscount: parseFloat(item.TotalAmountInclVATAfterDiscount || 0),
    }));

    // Prepare customer data
    const customer = ticket.Customer || (ticket.CustomerId ? {
      Id: ticket.CustomerId,
      Name: ticket.CustomerName || 'Walk-in Customer'
    } : null);

    // Use store info
    const company = storeInfo || ticket.CompanyInfo || {
      CompanyName: 'Your Store',
      Street: 'Street Address',
      City: 'City',
      PostCode: 'Postal Code',
      PhoneNumber: 'Phone',
      BTWNumber: 'VAT Number',
    };

    // ✅ Use PaymentMethodName from database instead of falling back to 'CASH'
    const paymentMethod = ticket.PaymentMethodName || ticket.PaymentMethod || 'CASH';
    // ✅ Use actual paid amount, don't fallback to total (to correctly show unpaid tickets)
    const paidAmount = parseFloat(ticket.PaidAmount || 0);
    const receiptNumber = ticket.ReceiptNumber || ticket.TicketNumber;

    // ✅ Database totals (available but template will recalculate from cart items)
    // ticket.VATAmount - Total VAT amount from database
    // ticket.TotalAmountInclVAT - Total including VAT before discount from database
    // ticket.TotalAmountInclVATAfterDiscount - Final total after discount from database
    // ticket.Subtotal - Subtotal excluding VAT from database

    // Map discount data
    const discount = ticket.DiscountAmount && ticket.DiscountAmount > 0 ? {
      type: ticket.DiscountType?.toLowerCase() === 'percentage' ? 'percentage' : 'fixed',
      value: ticket.DiscountType?.toLowerCase() === 'percentage'
        ? parseFloat(ticket.DiscountPercentage || 0)
        : parseFloat(ticket.DiscountAmount || 0),
      amount: parseFloat(ticket.DiscountAmount || 0),
      reason: ticket.DiscountReason || '',
    } : null;

    const customVATRate = ticket.VATPercentage !== null && ticket.VATPercentage !== undefined
      ? parseFloat(ticket.VATPercentage)
      : null;

    const notes = ticket.Notes || '*** THANK YOU FOR YOUR VISIT ***';

    // ✅ Map backend TicketType to display format (handles both string and integer)
    const normalizedType = normalizeTicketType(ticket.TicketType);
    const isRepairTicket = normalizedType === TICKET_TYPE.REPAIR;
    const pickupDate = ticket.PickupDate || null;
    const walkInCustomerName = !customer ? (ticket.CustomerName || '') : '';
    const customerPhone = ticket.CustomerPhone || customer?.PhoneNumber || customer?.Phone || '';

    // ✅ Prepare payment method object
    const paymentMethodObj = paymentMethod ? {
      name: paymentMethod,
      Name: paymentMethod
    } : null;

    // ✅ Pre-calculated totals from database to prevent recalculation rounding differences
    const preCalculatedTotals = {
      subtotal: parseFloat(ticket.Subtotal || 0),
      subtotalAfterDiscount: parseFloat(ticket.Subtotal || 0) - parseFloat(ticket.DiscountAmount || 0),
      totalVAT: parseFloat(ticket.VATAmount || 0),
      totalIncl: parseFloat(ticket.TotalAmountInclVATAfterDiscount || ticket.TotalAmountInclVAT || 0),
    };

    let templateHtml;

    if (format === 'a4') {
      // A4 template still uses old method
      templateHtml = A4InvoiceTemplate(
        cart,
        customer,
        company,
        paymentMethod,
        paidAmount,
        receiptNumber,
        discount,
        customVATRate,
        notes,
        undefined, // useMultiplePayments
        undefined, // paymentMethodsArray
        undefined, // paymentMethods
        walkInCustomerName,
        isRepairTicket ? 'Repair' : 'Sale',
        pickupDate
      );
    } else {
      // ✅ Use the same preview utilities based on ticket type
      if (isRepairTicket) {
        templateHtml = generateRepairTicketPreview({
          cart,
          customer,
          customerName: customer?.Name || ticket.CustomerName || '',
          customerPhone,
          storeInfo: company,
          receiptNumber,
          paidAmount,
          paymentMethod: paymentMethodObj,
          allPaymentMethods: paymentMethodsFromStore,
          discount,
          customVATRate,
          notes,
          pickupDate,
          isTvaIncluded: false, // Database stores base prices
          preCalculatedTotals, // ✅ Use database totals to prevent recalculation
        });
      } else {
        templateHtml = generateSaleTicketPreview({
          cart,
          customer,
          storeInfo: company,
          receiptNumber,
          paidAmount,
          paymentMethod: paymentMethodObj,
          allPaymentMethods: paymentMethodsFromStore,
          useMultiplePayments: false,
          paymentMethodsArray: [],
          discount,
          customVATRate,
          notes,
          walkInCustomerName,
          isTvaIncluded: false, // Database stores base prices
          preCalculatedTotals, // ✅ Use database totals to prevent recalculation
        });
      }
    }

    if (action === 'preview') {
      setPreviewHtml(templateHtml);
      setPreviewOpen(true);
    } else if (action === 'print') {
      await printDocument(templateHtml, format);
    }
  };

  const printDocument = async (templateHtml, format) => {
    try {
      const { jsPDF } = await import('jspdf');
      const html2canvas = (await import('html2canvas')).default;


      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = templateHtml;
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.width = format === 'a4' ? '210mm' : '80mm';
      tempDiv.style.background = 'white';
      tempDiv.style.padding = format === 'a4' ? '20mm' : '10px';
      document.body.appendChild(tempDiv);

      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      document.body.removeChild(tempDiv);

      const imgData = canvas.toDataURL('image/png');

      let pdf;
      if (format === 'a4') {
        pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4',
        });
        const imgWidth = 210;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      } else {
        const pdfHeight = (canvas.height * 80) / canvas.width;
        pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: [80, pdfHeight],
        });
        pdf.addImage(imgData, 'PNG', 0, 0, 80, pdfHeight);
      }

      pdf.autoPrint();
      window.open(pdf.output('bloburl'), '_blank');

      toast.success(t('print_sent') || 'Print job sent successfully');

      if (previewOpen) {
        setPreviewOpen(false);
      }
    } catch (error) {
      console.error('❌ Print error:', error);
      toast.error(t('print_failed') || 'Failed to print document');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const getPaymentMethodColor = (method) => {
    const colors = {
      CASH: 'success',
      BANCONTACT: 'primary',
      VISA: 'info',
      MASTERCARD: 'warning',
      CHEQUE: 'secondary',
      VIREMENT: 'default',
      PREPAID: 'info',
    };
    return colors[method?.toUpperCase()] || 'default';
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !ticket) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          {error || t('ticket_not_found') || 'Ticket not found'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mt: 2 }}
        >
          {t('back_to_tickets') || 'Back to Tickets'}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handleBack} sx={{ bgcolor: '#f5f5f5' }}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                {t('ticket_details') || 'Ticket Details'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {ticket.TicketNumber}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {ticket.Status !== 'Paid' && ticket.Status !== 'paid' && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<PaymentIcon />}
                onClick={handleOpenPaymentDialog}
                sx={{ borderRadius: 2 }}
              >
                {t('make_payment') || 'Make Payment'}
              </Button>
            )}
            <Button
              variant="contained"
              color="success"
              startIcon={<ReceiptLongIcon />}
              onClick={handleConvertToInvoice}
              sx={{ borderRadius: 2 }}
            >
              {t('convert_to_invoice') || 'Convert To Invoice'}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PrintIcon />}
              onClick={handlePrintClick}
              sx={{ borderRadius: 2 }}
            >
              {t('print') || 'Print'}
            </Button>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<EditIcon />}
              onClick={handleEdit}
              sx={{ borderRadius: 2 }}
            >
              {t('edit') || 'Edit'}
            </Button>
            <Button
              variant="outlined"
              color={showDeleteButton ? 'error' : 'inherit'}
              onClick={() => setShowDeleteButton(!showDeleteButton)}
              sx={{ borderRadius: 2 }}
            >
              {t('mark') || 'Mark'}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e3f2fd', borderLeft: '4px solid', borderColor: 'primary.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ReceiptIcon sx={{ color: 'primary.main' }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {t('ticket_number') || 'Ticket Number'}
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {ticket.TicketNumber || '-'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e8f5e9', borderLeft: '4px solid', borderColor: 'success.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <MoneyIcon sx={{ color: 'success.main' }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {t('total_amount') || 'Total Amount'}
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                {formatCurrency(ticket.TotalAmountInclVATAfterDiscount || ticket.TotalAmountInclVAT || ticket.TotalAmount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#fff3e0', borderLeft: '4px solid', borderColor: 'warning.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PaymentIcon sx={{ color: 'warning.main' }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {t('payment_method') || 'Payment Method'}
                </Typography>
              </Box>
              <Chip
                label={(ticket.PaymentMethodName || ticket.PaymentMethod || 'CASH').toUpperCase()}
                color={getPaymentMethodColor(ticket.PaymentMethod)}
                sx={{ fontWeight: 600, fontSize: '0.875rem' }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#f3e5f5', borderLeft: '4px solid', borderColor: 'secondary.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CalendarIcon sx={{ color: 'secondary.main' }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {t('date_time') || 'Date & Time'}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formatDate(ticket.CreatedDate || ticket.Date)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatTime(ticket.CreatedDate || ticket.Date)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Ticket Information */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          {t('ticket_information') || 'Ticket Information'}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {t('ticket_type') || 'Ticket Type'}
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={getTicketTypeLabel(ticket.TicketType, t)}
                  color={getTicketTypeColor(ticket.TicketType)}
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {t('customer') || 'Customer'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <PersonIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {ticket.CustomerName || t('walk_in_customer') || 'Walk-in Customer'}
                </Typography>
              </Box>
              {ticket.CustomerPhone && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
                  {ticket.CustomerPhone}
                </Typography>
              )}
            </Box>

            {normalizeTicketType(ticket.TicketType) === TICKET_TYPE.REPAIR && ticket.PickupDate && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {t('pickup_date') || 'Pickup Date'}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                  {new Date(ticket.PickupDate).toLocaleDateString()}
                </Typography>
              </Box>
            )}

            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {t('subtotal_excl_vat') || 'Subtotal (Excl. VAT)'}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                {formatCurrency(ticket.Subtotal)}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {t('vat_amount') || 'VAT Amount'}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                {formatCurrency(ticket.VATAmount)} ({ticket.VATPercentage}%)
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            {ticket.DiscountAmount && ticket.DiscountAmount > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {t('discount') || 'Discount'}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5, color: 'success.main' }}>
                  -{formatCurrency(ticket.DiscountAmount)}
                  {ticket.DiscountType === 'Percentage' && ` (${ticket.DiscountPercentage}%)`}
                </Typography>
                {ticket.DiscountReason && (
                  <Typography variant="caption" color="text.secondary">
                    {ticket.DiscountReason}
                  </Typography>
                )}
              </Box>
            )}

            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {t('paid_amount') || 'Paid Amount'}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                {formatCurrency(ticket.PaidAmount || 0)}
              </Typography>
            </Box>

            {((ticket.TotalAmountInclVATAfterDiscount || ticket.TotalAmountInclVAT) - (ticket.PaidAmount || 0)) > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {t('due_amount') || 'Due Amount'}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.5, color: 'error.main' }}>
                  {formatCurrency((ticket.TotalAmountInclVATAfterDiscount || ticket.TotalAmountInclVAT) - (ticket.PaidAmount || 0))}
                </Typography>
              </Box>
            )}

            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {t('payment_status') || 'Payment Status'}
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={ticket.Status || 'Completed'}
                  color={ticket.Status === 'Paid' ? 'success' : ticket.Status === 'Partial Paid' ? 'warning' : 'default'}
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>

        {ticket.Notes && (
          <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {t('notes') || 'Notes'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
              {ticket.Notes}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Payment History Section */}
      {ticket.PaymentDetails && ticket.PaymentDetails.length > 0 && (
        <Paper sx={{ p: 3, borderRadius: 2, mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <PaymentIcon sx={{ color: '#667eea', mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#667eea' }}>
              {t('payment_history') || 'Payment History'}
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)' }}>
                  <TableCell sx={{ fontWeight: 600 }}>{t('payment_method') || 'Payment Method'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{t('amount_paid') || 'Amount Paid'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>{t('payment_date') || 'Payment Date'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('notes') || 'Notes'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ticket.PaymentDetails.map((payment, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Chip 
                        label={payment.PaymentMethodName || 'Unknown Method'}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight={600} sx={{ color: '#4caf50' }}>
                        {formatCurrency(payment.Amount || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {payment.PaymentDate ? new Date(payment.PaymentDate).toLocaleDateString() : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {payment.Notes || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals Row */}
                <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.15)' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>
                    {t('total_paid') || 'Total Paid'}:
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: '#4caf50' }}>
                    {formatCurrency(ticket.PaidAmount || 0)}
                  </TableCell>
                  <TableCell colSpan={2}>
                    {((ticket.TotalAmountInclVATAfterDiscount || ticket.TotalAmountInclVAT) - (ticket.PaidAmount || 0)) > 0 ? (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('remaining_due') || 'Remaining Due'}:
                        </Typography>
                        <Typography variant="h6" fontWeight={700} sx={{ color: '#f44336' }}>
                          {formatCurrency((ticket.TotalAmountInclVATAfterDiscount || ticket.TotalAmountInclVAT) - (ticket.PaidAmount || 0))}
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<PaymentIcon />}
                          onClick={handleOpenPaymentDialog}
                          sx={{ borderRadius: 2, ml: 2, fontWeight: 600 }}
                        >
                          {t('record_payment') || 'Record Payment'}
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                        <Chip
                          label={t('fully_paid') || 'Fully Paid'}
                          color="success"
                          icon={<PaymentIcon />}
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Line Items */}
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          {t('line_items') || 'Line Items'}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('product_service') || 'Product/Service'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('details') || 'Details'}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>{t('quantity') || 'Qty'}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{t('unit_price') || 'Unit Price'}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{t('vat') || 'VAT'}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{t('discount') || 'Discount'}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{t('total') || 'Total'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(ticket.LineItems || ticket.Items || []).map((item, index) => (
                <TableRow key={index} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.ServiceName || item.ProductName || item.Name || 'Product'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {item.ServiceDescription && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {item.ServiceDescription}
                      </Typography>
                    )}
                    {item.ProductSerial && (
                      <Chip
                        label={`SN: ${item.ProductSerial}`}
                        size="small"
                        variant="outlined"
                        sx={{ mt: 0.5, fontSize: '0.7rem' }}
                      />
                    )}
                    {!item.ServiceDescription && !item.ProductSerial && '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={item.Quantity}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(item.UnitPriceExclTax || item.UnitPrice)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(item.VATAmount || 0)}
                  </TableCell>
                  <TableCell align="right">
                    {item.DiscountAmount > 0 
                      ? formatCurrency(item.DiscountAmount)
                      : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
                      {formatCurrency(item.TotalAmountInclVATAfterDiscount || item.TotalAmountInclVAT || item.TotalPrice)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}

              {/* Total Row */}
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell colSpan={7} align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                  {t('grand_total') || 'GRAND TOTAL'}:
                </TableCell>
                <TableCell align="right">
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                    {formatCurrency(ticket.TotalAmountInclVATAfterDiscount || ticket.TotalAmountInclVAT || ticket.TotalAmount)}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Delete Button Section */}
      {showDeleteButton && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
            sx={{ borderRadius: 2 }}
          >
            {t('delete_ticket') || 'Delete Ticket'}
          </Button>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main', fontWeight: 600 }}>
          {t('confirm_delete') || 'Confirm Delete'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('confirm_delete_ticket_message', { ticket: ticket.TicketNumber }) || 
             `Are you sure you want to delete ticket ${ticket.TicketNumber}? This action cannot be undone.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleDeleteCancel}
            color="inherit"
            disabled={deleting}
          >
            {t('cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? t('deleting') || 'Deleting...' : t('delete') || 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Print Options Dialog */}
      <PrintOptionsDialog
        open={printOptionsOpen}
        onClose={() => setPrintOptionsOpen(false)}
        onSelectFormat={handleFormatSelection}
      />

      {/* Preview Dialogs */}
      {currentFormat === 'a4' ? (
        <A4PreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          htmlContent={previewHtml}
          onPrint={() => printDocument(previewHtml, currentFormat)}
        />
      ) : (
        <PreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          htmlContent={previewHtml}
          onPrint={() => printDocument(previewHtml, currentFormat)}
          format="receipt"
        />
      )}

      {/* Payment Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={handleClosePaymentDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <PaymentIcon />
          {t('record_payment') || 'Record Payment'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 2 }}>
          <Grid container spacing={3}>
            {/* Payment Amount */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('payment_amount') || 'Payment Amount'}
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
                inputProps={{
                  min: 0.01,
                  step: 0.01
                }}
                required
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 2 }
                }}
              />
            </Grid>

            {/* Payment Date */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('payment_date') || 'Payment Date'}
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 2 }
                }}
              />
            </Grid>

            {/* Payment Method */}
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>{t('payment_method') || 'Payment Method'}</InputLabel>
                <Select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  label={t('payment_method') || 'Payment Method'}
                  sx={{ borderRadius: 2 }}
                >
                  {paymentMethodsFromStore.map((pm) => (
                    <MenuItem key={pm.id} value={pm.name}>
                      {pm.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Payment Reference */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('payment_reference') || 'Payment Reference (Optional)'}
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                placeholder="e.g., Transaction ID, Check number"
                inputProps={{ maxLength: 500 }}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 2 }
                }}
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('notes') || 'Notes (Optional)'}
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                multiline
                rows={3}
                placeholder="Add any additional notes about this payment"
                inputProps={{ maxLength: 500 }}
                helperText={`${paymentNotes.length}/500`}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 2 }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={handleClosePaymentDialog}
            variant="outlined"
            disabled={paymentLoading}
            sx={{ borderRadius: 2 }}
          >
            {t('cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleSubmitPayment}
            variant="contained"
            disabled={paymentLoading || !paymentAmount || parseFloat(paymentAmount) <= 0 || !paymentMethod || !paymentDate}
            startIcon={paymentLoading ? <CircularProgress size={16} /> : <PaymentIcon />}
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              }
            }}
          >
            {paymentLoading ? (t('processing') || 'Processing...') : `${t('record_payment') || 'Record Payment'} €${parseFloat(paymentAmount || 0).toFixed(2)}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TicketDetailPage;
